import clonedeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { TrackingEventList } from 'portal-sdk';
import { query as stardogQuery } from 'stardog';
import { VoiceboxResponseActionType } from 'vet-bones/constants/voicebox';
import { sendHubspotEvent } from 'vet-bones/utils/portal';

import {
  DatabasesAction,
  DatabasesActionType,
} from 'src/common/actions/databases/databasesActionCreators';
import {
  FileSystemAction,
  FileSystemActionType,
} from 'src/common/actions/fileSystem/fileSystemActionCreators';
import {
  NotebookAction,
  NotebookActionType,
  notebookActionCreators,
  notebookStardogRequestDispatchers,
} from 'src/common/actions/notebook/notebookActionCreators';
import {
  PreferencesAction,
  PreferencesActionType,
} from 'src/common/actions/preferences/preferencesActionCreators';
import {
  QueriesAction,
  QueriesActionType,
} from 'src/common/actions/queries/queriesActionCreators';
import { Response, Unwrapped } from 'src/common/actions/request/types';
import {
  StoredQueriesAction,
  StoredQueriesActionType,
} from 'src/common/actions/storedQueries/storedQueriesActionCreators';
import {
  VisualizationsAction,
  VisualizationsActionType,
} from 'src/common/actions/visualizations/visualizationsActionCreators';
import { LanguageId } from 'src/common/constants/LanguageId';
import { FAILURE_CONTENT_TYPE } from 'src/common/constants/notebook/editorConstants';
import {
  PREFERENCES_NOTE,
  PREFERENCES_NOTE_ID,
  RELEASE_NOTES_NOTE,
} from 'src/common/constants/notebook/noteConstants';
import { QueryType, QueryTypeIdentifier } from 'src/common/constants/QueryType';
import { GLOBAL_SYMBOL } from 'src/common/constants/storedQueries/storedQueriesConstants';
import { DEFAULT_REASONING_SCHEMA } from 'src/common/store/databases/DatabasesState';
import {
  NotebookState,
  PaneStateMap,
  defaultResultsHeight,
  defaultSanddanceResultsHeight,
  defaultVisualizationResultsHeight,
  minResultsHeight,
} from 'src/common/store/notebook/NotebookState';
import {
  EditorSettings,
  JsonLdBody,
  NoteState,
  NoteStorageType,
  ResultSettings,
  ResultViewType,
  defaultVoiceboxSettings,
} from 'src/common/store/notebook/NoteState';
import {
  RawJsonLdData,
  RawJsonLdResult,
  VisualizationSourceType,
} from 'src/common/store/visualization/VisualizationState';
import {
  getTabId,
  getUniqueTabNameForStorageType,
} from 'src/common/utils/notes/tabId';
import {
  addNewTab,
  addTab,
  getUntitledTabName,
  removeTab,
} from 'src/common/utils/notes/tabs';
import { getPreferencesQuery } from 'src/common/utils/preferences/getPreferencesQuery';
import { safelyGet } from 'src/common/utils/safelyGet';

const getEmptyBodyForQueryType = (queryType: QueryType) =>
  queryType === QueryTypeIdentifier.SELECT ||
  queryType === QueryTypeIdentifier.PATHS ||
  queryType === QueryTypeIdentifier.GRAPHQL
    ? {}
    : '';

const getQueryType = (languageId, query) =>
  languageId && languageId === QueryTypeIdentifier.GRAPHQL
    ? QueryTypeIdentifier.GRAPHQL
    : stardogQuery.utils.queryType(query);

const updateNoteState = (
  prevState: NotebookState,
  noteId: NoteState['id'],
  parentKey: keyof Pick<NoteState, 'editorSettings' | 'resultSettings'>,
  update: Partial<EditorSettings | ResultSettings>,
  incrementQueries = false
): NotebookState => {
  const currentNote = prevState.notes[noteId];

  if (!currentNote) {
    return prevState;
  }

  const nextNote = clonedeep<NoteState>(currentNote);
  nextNote.totalQueries = currentNote.totalQueries + (incrementQueries ? 1 : 0);
  nextNote[parentKey] = {
    ...currentNote[parentKey],
    ...(update as any),
  };

  return {
    ...prevState,
    notes: {
      ...prevState.notes,
      [noteId]: nextNote,
    },
  };
};

const parseGraphqlTextExplanation = ({
  plan = '',
  fields = {},
  sparql = '',
}) => {
  return `SPARQL:
${sparql}
FIELDS:
${JSON.stringify(fields, null, 4)}
PLAN:
${plan}
`.replace(/\n\s*\n\s*\n/g, '\n\n'); // remove duplicate empty lines
};

export const initialState = addNewTab(new NotebookState());

export const notebookReducer = (
  prevState = initialState,
  action:
    | NotebookAction
    | QueriesAction
    | FileSystemAction
    | StoredQueriesAction
    | PreferencesAction
    | DatabasesAction
    | VisualizationsAction
): NotebookState => {
  const noteId: string = action.payload
    ? // TODO: Could have better types here, I imagine.
      (action.payload as any).action
      ? (action.payload as any).action.noteId
      : (action as any).payload.noteId
    : prevState.activeNoteId;

  switch (action.type) {
    case NotebookActionType.ASK_VOICEBOX_ATTEMPT: {
      const currentNote = prevState.notes[noteId];

      if (!currentNote) {
        return prevState;
      }

      const nextNote = clonedeep<NoteState>(currentNote);
      nextNote.resultSettings = {
        ...currentNote.resultSettings,
        pending: true,
      };
      nextNote.voiceboxSettings = {
        ...currentNote.voiceboxSettings,
        pending: true,
      };

      return {
        ...prevState,
        notes: {
          ...prevState.notes,
          [noteId]: nextNote,
        },
      };
    }
    case NotebookActionType.ASK_VOICEBOX_SUCCESS:
    case NotebookActionType.ASK_VOICEBOX_FAILURE: {
      const currentNote = prevState.notes[noteId];

      if (!currentNote) {
        return prevState;
      }

      const nextNote = clonedeep<NoteState>(currentNote);
      nextNote.resultSettings = {
        ...currentNote.resultSettings,
        pending: false,
      };
      nextNote.voiceboxSettings = {
        ...currentNote.voiceboxSettings,
        pending: false,
      };

      return {
        ...prevState,
        notes: {
          ...prevState.notes,
          [noteId]: nextNote,
        },
      };
    }
    case NotebookActionType.TOGGLED_VOICEBOX: {
      const { isOpen } = action.payload;
      const currentNote = prevState.notes[noteId];

      if (!currentNote) {
        return prevState;
      }

      const nextNote = clonedeep<NoteState>(currentNote);
      nextNote.voiceboxSettings = {
        ...currentNote.voiceboxSettings,
        isOpen,
      };

      return {
        ...prevState,
        notes: {
          ...prevState.notes,
          [noteId]: nextNote,
        },
      };
    }
    case NotebookActionType.UPDATED_VOICEBOX_CONVERSATION_ID: {
      const { connectionIdx, conversationId } = action.payload;
      const currentNote = prevState.notes[noteId];

      if (!currentNote) {
        return prevState;
      }

      return {
        ...prevState,
        notes: {
          ...prevState.notes,
          [noteId]: {
            ...currentNote,
            voiceboxSettings: {
              ...currentNote.voiceboxSettings,
              connectionIdx,
              conversationId,
            },
          },
        },
      };
    }
    case NotebookActionType.UPDATED_VOICEBOX_MESSAGE_QUERY: {
      const { connectionIdx, message, noteId } = action.payload;
      const currentNote = prevState.notes[noteId];

      if (!currentNote) {
        return prevState;
      }

      let query = '';
      const sparql = message.actions?.find(
        (action) => action.type === VoiceboxResponseActionType.SPARQL
      )?.value;
      if (sparql) {
        query = sparql;
      }

      return {
        ...prevState,
        notes: {
          ...prevState.notes,
          [noteId]: {
            ...currentNote,
            editorSettings: {
              ...currentNote.editorSettings,
              ...(query ? { query } : {}),
            },
            voiceboxSettings: {
              ...currentNote.voiceboxSettings,
              connectionIdx,
              conversationId: message.conversationId,
            },
          },
        },
      };
    }
    case NotebookActionType.SET_DB_FOR_NOTE: {
      const currentNote = prevState.notes[noteId];

      if (!currentNote) {
        return prevState;
      }

      const previouslyActiveDatabase =
        currentNote.editorSettings.activeDatabase;

      const nextNote = clonedeep<NoteState>(currentNote);
      nextNote.editorSettings = {
        ...prevState.notes[noteId].editorSettings,
        activeDatabase: action.payload.db,
        namedGraphs: [],
        schema:
          previouslyActiveDatabase !== action.payload.db
            ? DEFAULT_REASONING_SCHEMA
            : prevState.notes[noteId].editorSettings.schema,
      };
      nextNote.resultSettings = {
        ...prevState.notes[noteId].resultSettings,
        // Clear any results hanging around if the DB is changed (#98)
        body: undefined,
        contentType: null,
      };
      nextNote.voiceboxSettings = {
        ...defaultVoiceboxSettings,
        // close the voicebox tab if it is open
        isOpen: false,
      };

      return {
        ...prevState,
        notes: {
          ...prevState.notes,
          [noteId]: nextNote,
        },
      };
    }
    case NotebookActionType.SET_NAMED_GRAPHS_FOR_NOTE:
      return updateNoteState(prevState, noteId, 'editorSettings', {
        namedGraphs: action.payload.namedGraphs,
      });
    case NotebookActionType.TOGGLE_REASONING_FOR_NOTE:
      return updateNoteState(prevState, noteId, 'editorSettings', {
        reasoning: !prevState.notes[noteId].editorSettings.reasoning,
      });
    case NotebookActionType.SET_QUERY_TIMEOUT_FOR_NOTE:
      return updateNoteState(prevState, noteId, 'editorSettings', {
        timeout: action.payload.timeout,
      });
    case NotebookActionType.SET_REASONING_SCHEMA_FOR_NOTE:
      return updateNoteState(prevState, noteId, 'editorSettings', {
        schema: action.payload.schema,
      });
    case NotebookActionType.SET_QUERY_FOR_NOTE:
      return updateNoteState(prevState, noteId, 'editorSettings', {
        query: action.payload.query,
      });
    case NotebookActionType.SET_QUERY_ID_FOR_NOTE:
      return updateNoteState(prevState, noteId, 'resultSettings', {
        queryId:
          (
            action as ReturnType<
              typeof notebookActionCreators['setQueryIdForNote']
            >
          ).payload.queryId ||
          safelyGet(
            (action as unknown as Response<any, any, any>).payload.response,
            ['headers', 'sd-query-id', 0]
          ),
      });
    case QueriesActionType.KILL_QUERY_SUCCESS: {
      const { associatedNoteId } = action.payload.action;

      if (typeof associatedNoteId === 'undefined') {
        return prevState;
      }

      return updateNoteState(prevState, associatedNoteId, 'resultSettings', {
        pending: false,
      });
    }
    case QueriesActionType.KILL_QUERIES_FAILURE:
    case QueriesActionType.KILL_QUERIES_SUCCESS: {
      const { successAssociatedNoteIds } = action.payload;

      return Object.keys(successAssociatedNoteIds).reduce(
        (accState, associatedNoteId) => {
          if (typeof associatedNoteId === 'undefined') {
            return accState;
          }

          return updateNoteState(accState, associatedNoteId, 'resultSettings', {
            pending: false,
          });
        },
        prevState
      );
    }
    case DatabasesActionType.VALIDATE_CONSTRAINTS_ATTEMPT: {
      return updateNoteState(prevState, noteId, 'resultSettings', {
        typeOfQuery: QueryTypeIdentifier.SHACL_REPORT,
        contentType: null,
        body: getEmptyBodyForQueryType(QueryTypeIdentifier.SHACL_REPORT),
        jsonLdBody: undefined,
        languageId: prevState.notes[noteId].editorSettings.languageId,
        reasoning: prevState.notes[noteId].editorSettings.reasoning,
        database: prevState.notes[noteId].editorSettings.activeDatabase,
        pending: true,
        resultsProcessed: false,
        viewType: ResultViewType.TEXTUAL,
      });
    }
    case NotebookActionType.EXECUTE_QUERY_FOR_NOTE_ATTEMPT: {
      let query: string;
      let queryId: string;

      if ((action as any).payload.args) {
        const { payload } = action as Response<any, any, any>;
        query = payload.args[1];
        queryId = (payload.args[3] || {}).id;
      } else {
        const { payload } = action as ReturnType<
          typeof notebookActionCreators['executeSelectAttempt']
        >;
        ({ query, queryId } = payload);
      }

      const queryType = getQueryType(
        safelyGet(action.payload as any, ['action', 'languageId'], null),
        query
      );

      const noteStateResultsUpdate = {
        query,
        // Set this only in the attempt as we have all the necessary knowledge
        // (the query) and that doesn't change between attempt and success or
        // failure.
        typeOfQuery: queryType,
        contentType: null,
        body: getEmptyBodyForQueryType(queryType),
        jsonLdBody: undefined,
        languageId: prevState.notes[noteId].editorSettings.languageId,
        reasoning: prevState.notes[noteId].editorSettings.reasoning,
        database: prevState.notes[noteId].editorSettings.activeDatabase,
        pending: true,
        resultsProcessed: false,
        viewType: ResultViewType.TEXTUAL,
      };

      if (queryId) {
        (noteStateResultsUpdate as any).queryId = queryId;
      }

      return updateNoteState(
        prevState,
        noteId,
        'resultSettings',
        noteStateResultsUpdate
      );
    }
    case NotebookActionType.EXECUTE_JSON_LD_QUERY_FOR_NOTE_ATTEMPT: {
      return updateNoteState(prevState, noteId, 'resultSettings', {
        jsonLdBody: {
          queryHistoryId: action.payload.action.queryHistoryId,
        },
      });
    }
    case NotebookActionType.EXPLAIN_QUERY_FOR_NOTE_ATTEMPT: {
      const { noteId, profile, queryId } = action.payload.action;
      const query = (action as Response<any, any, any>).payload.args[1];
      const oldNote = prevState.notes[noteId];
      const oldResultSettingsToKeep =
        oldNote.resultSettings.typeOfQuery === QueryTypeIdentifier.EXPLAIN ||
        oldNote.resultSettings.typeOfQuery === QueryTypeIdentifier.PROFILE
          ? pick(oldNote.resultSettings, 'languageId')
          : null;

      return updateNoteState(prevState, noteId, 'resultSettings', {
        // Reset some stuff
        contentType: '',
        body: {},
        jsonLdBody: undefined,
        resultsProcessed: false,
        // Update some stuff, as these values affect the explanation
        query,
        queryId,
        ...oldResultSettingsToKeep,
        // Set this only in the attempt as we have all the necessary knowledge
        // (the query) and that doesn't change between attempt and success or
        // failure.
        typeOfQuery: profile
          ? QueryTypeIdentifier.PROFILE
          : QueryTypeIdentifier.EXPLAIN,
        reasoning: prevState.notes[noteId].editorSettings.reasoning,
        database: prevState.notes[noteId].editorSettings.activeDatabase,
        // State
        pending: true,
        viewType: ResultViewType.TEXTUAL,
        languageId: LanguageId.TXT,
      });
    }
    case NotebookActionType.EXPLAIN_QUERY_FOR_NOTE_SUCCESS: {
      const { response, action: actionPayload } = action.payload;
      const { queryLanguageId, profile } = actionPayload;
      const height =
        prevState.notes[noteId].resultSettings.height || defaultResultsHeight;
      let { body: responseBody } = response;
      if (queryLanguageId === LanguageId.GRAPHQL) {
        responseBody = parseGraphqlTextExplanation(responseBody.data);
      }
      return updateNoteState(
        prevState,
        noteId,
        'resultSettings',
        {
          // TODO this comment
          // `typeOfQuery` is only set here for tests in NoteResult.test.tsx
          // which only dispatch the `success` message and expect the state to
          // be fully set for the NoteResult component to render correctly.
          // The tests should be simulating the full series of messages (ATTEMPT -> RECEIVED -> SUCCESS | FAILURE)
          typeOfQuery: profile
            ? QueryTypeIdentifier.PROFILE
            : QueryTypeIdentifier.EXPLAIN,
          contentType: response.headers['content-type'],
          resultsProcessed: true,
          body: responseBody,
          isCollapsed: false,
          pending: false,
          height,
          heightPercent: null,
          queryId: undefined,
        },
        true
      );
    }
    case NotebookActionType.FETCH_FROM_CACHE: {
      const note = prevState.notes[noteId];
      const body = note.resultSettings.body as any;
      if (!body.results) {
        return prevState;
      }

      const { bindings } = body.results;
      const newBindings = [...bindings];
      newBindings.splice(
        action.payload.row,
        action.payload.bindings.length,
        ...action.payload.bindings
      );
      return updateNoteState(prevState, noteId, 'resultSettings', {
        body: {
          ...body,
          results: {
            ...body.results,
            bindings: newBindings,
          },
        },
      });
    }
    case NotebookActionType.EXECUTE_QUERY_FOR_NOTE_RECEIVED: {
      const query = action.payload.args[1];
      const queryType = getQueryType(
        safelyGet(action.payload as any, ['action', 'languageId'], null),
        query
      );

      const resultsHeight =
        prevState.notes[noteId].resultSettings.height || defaultResultsHeight;

      return updateNoteState(prevState, noteId, 'resultSettings', {
        isCollapsed: false,
        height: resultsHeight,
        heightPercent: null,
        // TODO: this is a hack, I think -- @mscandal said he might do something else here:
        body: getEmptyBodyForQueryType(queryType),
        viewType: ResultViewType.TEXTUAL,
        resultsProcessed: false,
        pending: false,
      });
    }
    case DatabasesActionType.VALIDATE_CONSTRAINTS_SUCCESS: {
      const { response } = action.payload;
      const resultsHeight =
        prevState.notes[noteId].resultSettings.height || defaultResultsHeight;

      return updateNoteState(prevState, noteId, 'resultSettings', {
        contentType: response.headers['content-type'],
        body: response?.body,
        isCollapsed: false,
        height: resultsHeight,
        heightPercent: null,
        pending: false,
        resultsProcessed: true,
        typeOfQuery: QueryTypeIdentifier.SHACL_REPORT,
      });
    }
    case NotebookActionType.EXECUTE_QUERY_FOR_NOTE_SUCCESS: {
      const { response, action: actionPayload } = action.payload;
      const query = (action as Response<any, any, any>).payload.args
        ? (action as Response<any, any, any>).payload.args[1]
        : (
            action as ReturnType<
              typeof notebookActionCreators['executeSelectSuccess']
            >
          ).payload.query;

      const queryType = getQueryType((actionPayload as any).languageId, query);

      type BandaidAction = Unwrapped<
        ReturnType<
          typeof notebookStardogRequestDispatchers['executeQueryDispatcher']
        >
      >;

      let body;
      switch (queryType) {
        case QueryTypeIdentifier.SELECT: {
          const bindings = [];
          for (
            let i = 0;
            i < response?.body?.results?.totalLength || 0;
            i += 1
          ) {
            // TODO: Someone please comment what's going on with the `false` push here.
            bindings.push(response?.body?.results?.bindings[i] || false);
          }
          body = {
            head: response?.body?.head,
            results: {
              bindings,
            },
          };
          break;
        }
        default:
          body = response?.body;
      }

      const resultsHeight =
        prevState.notes[noteId].resultSettings.height || defaultResultsHeight;
      sendHubspotEvent(TrackingEventList.STUDIO_SPARQL_QUERY_EXECUTION);

      return updateNoteState(
        prevState,
        noteId,
        'resultSettings',
        {
          // `typeOfQuery` is only set here for tests in NoteResult.test.tsx
          // which only dispatch the `success` message and expect the state to
          // be fully set for the NoteResult component to render correctly.
          // The tests should be simulating the full series of messages (ATTEMPT -> RECEIVED -> SUCCESS | FAILURE)
          typeOfQuery: queryType,
          contentType:
            queryType === QueryTypeIdentifier.UPDATE
              ? null
              : response.headers['content-type'][0],
          body,
          isCollapsed: false,
          height: resultsHeight,
          heightPercent: null,
          pending: false,
          resultsProcessed: true,
          scrollPosition: [0, 0, 0, 0],
          selection: undefined,
          timeElapsed: action.payload.timeElapsed,
          pathStart: (action as BandaidAction).payload.action.start,
          pathEnd: (action as BandaidAction).payload.action.end,
          viewType: ResultViewType.TEXTUAL,
          queryId: undefined,
        },
        true
      );
    }
    case NotebookActionType.EXECUTE_JSON_LD_QUERY_FOR_NOTE_SUCCESS: {
      type BandaidAction = Unwrapped<
        ReturnType<
          typeof notebookStardogRequestDispatchers['executeJsonLdQueryDispatcher']
        >
      >;
      const { response, action: actionPayload } = (action as BandaidAction)
        .payload;

      const result: RawJsonLdData[] | RawJsonLdResult = response?.body || [];
      const results = Array.isArray(result) ? result : result['@graph'];

      const prevJsonLdBody: Partial<JsonLdBody> =
        prevState.notes[noteId]?.resultSettings?.jsonLdBody || {};
      return updateNoteState(prevState, noteId, 'resultSettings', {
        jsonLdBody: {
          ...prevJsonLdBody,
          queryHistoryId:
            prevJsonLdBody.queryHistoryId || actionPayload.queryHistoryId,
          results,
        },
        queryId: undefined,
      });
    }
    case DatabasesActionType.VALIDATE_CONSTRAINTS_FAILURE: {
      return updateNoteState(prevState, noteId, 'resultSettings', {
        pending: false,
      });
    }
    case NotebookActionType.EXECUTE_QUERY_FOR_NOTE_FAILURE:
    case NotebookActionType.EXECUTE_JSON_LD_QUERY_FOR_NOTE_FAILURE: {
      const { response } = (action as Response<any, any, any>).payload;
      const resultsHeight =
        prevState.notes[noteId].resultSettings.height || defaultResultsHeight;
      return updateNoteState(prevState, noteId, 'resultSettings', {
        body: response?.body,
        jsonLdBody: undefined,
        contentType: FAILURE_CONTENT_TYPE,
        pending: false,
        height: resultsHeight,
        heightPercent: null,
        resultsProcessed: true,
        viewType: ResultViewType.TEXTUAL,
        isCollapsed: false,
        queryId: undefined,
      });
    }
    case NotebookActionType.EXPLAIN_QUERY_FOR_NOTE_FAILURE: {
      const { response, action: actionPayload } = action.payload;
      const { profile } = actionPayload;
      const resultsHeight =
        prevState.notes[noteId].resultSettings.height || defaultResultsHeight;
      return updateNoteState(prevState, noteId, 'resultSettings', {
        typeOfQuery: profile
          ? QueryTypeIdentifier.PROFILE
          : QueryTypeIdentifier.EXPLAIN,
        // TODO why any
        body: (response as any).body,
        jsonLdBody: undefined,
        contentType: FAILURE_CONTENT_TYPE,
        pending: false,
        height: resultsHeight,
        heightPercent: null,
        resultsProcessed: true,
        isCollapsed: false,
        queryId: undefined,
      });
    }
    case NotebookActionType.EXPORT_RESULTS_FOR_NOTE_ATTEMPT:
      return updateNoteState(prevState, noteId, 'resultSettings', {
        pending: true,
      });
    case NotebookActionType.EXPORT_RESULTS_FOR_NOTE_FAILURE:
    case NotebookActionType.EXPORT_RESULTS_FOR_NOTE_SUCCESS:
      return updateNoteState(prevState, noteId, 'resultSettings', {
        pending: false,
      });
    case VisualizationsActionType.VISUALIZE_RESULTS_ATTEMPT: {
      const { type, noteId } = action.payload.source;
      if (type === VisualizationSourceType.NOTE) {
        const height = Math.max(
          prevState.notes[noteId].resultSettings.height,
          defaultVisualizationResultsHeight
        );

        return updateNoteState(prevState, noteId, 'resultSettings', {
          viewType: ResultViewType.VISUAL,
          height,
          heightPercent: null,
        });
      }
      return prevState;
    }
    case VisualizationsActionType.VISUALIZE_RESULTS_SUCCESS: {
      const { type, noteId } = action.payload.source;
      if (type === VisualizationSourceType.NOTE) {
        const height = Math.max(
          prevState.notes[noteId].resultSettings.height,
          defaultVisualizationResultsHeight
        );

        return updateNoteState(prevState, noteId, 'resultSettings', {
          viewType: ResultViewType.VISUAL,
          height,
          heightPercent: null,
        });
      }
      return prevState;
    }
    case NotebookActionType.ADD_NOTE: {
      return addNewTab(prevState);
    }
    case NotebookActionType.DELETE_FILE_HANDLE_FOR_NOTE: {
      const { noteId } = action.payload;
      const prevNote = prevState.notes[noteId];
      const nextEditorSettings = {
        ...prevNote.editorSettings,
      };
      delete nextEditorSettings.handle;
      const nextNote = clonedeep(prevNote);
      nextNote.editorSettings = nextEditorSettings;

      return {
        ...prevState,
        notes: {
          ...prevState.notes,
          [noteId]: nextNote,
        },
      };
    }
    case NotebookActionType.DELETE_NOTE: {
      return removeTab(prevState, noteId);
    }
    case NotebookActionType.TOGGLE_RESULT_COLLAPSE_FOR_NOTE: {
      const { isCollapsed: prevCollapsed, height: resultsHeight } =
        prevState.notes[noteId].resultSettings;

      const nextCollapsed = !prevCollapsed;

      let height;
      if (!nextCollapsed && resultsHeight < defaultResultsHeight) {
        // if user resized results to the point of collapsing them, when they're toggled back open, jump to default height
        height = defaultResultsHeight;
      } else {
        height = resultsHeight;
      }

      return updateNoteState(prevState, noteId, 'resultSettings', {
        isCollapsed: nextCollapsed,
        height,
        heightPercent: null,
      });
    }
    case NotebookActionType.REARRANGE_NOTES: {
      const { sourceNoteId, targetNoteId } = action.payload;
      const indexOfTarget = prevState.noteIds.indexOf(targetNoteId);

      const newNoteIds = prevState.noteIds.filter((id) => id !== sourceNoteId);
      newNoteIds.splice(indexOfTarget, 0, sourceNoteId);

      return {
        ...prevState,
        noteIds: newNoteIds,
        activeNoteId: sourceNoteId,
      };
    }
    case NotebookActionType.RENAME_NOTE: {
      const { noteId, newName: tenativeNewName } = action.payload;
      const note = prevState.notes[noteId];
      if (!note) {
        return prevState;
      }

      const newName = getUniqueTabNameForStorageType(
        new Set(prevState.noteIds),
        note.storageType,
        tenativeNewName
      );
      const newNoteId = getTabId(note.storageType, newName);

      const noteIdx = prevState.noteIds.indexOf(noteId);
      const nextNoteIds = [...prevState.noteIds];
      nextNoteIds.splice(noteIdx, 1, newNoteId);

      const nextPanes = Object.keys(prevState.panes).reduce<PaneStateMap>(
        (acc, key) => {
          const noteIds = [...prevState.panes[key].noteIds];
          const noteIdx = noteIds.indexOf(noteId);
          noteIds.splice(noteIdx, 1, newNoteId);
          acc[key] = { noteIds };
          return acc;
        },
        {}
      );

      const nextNote = clonedeep(prevState.notes[noteId]);
      nextNote.id = newNoteId;
      nextNote.name = newName;
      nextNote.isUntitled = false;
      const nextNotes = {
        ...omit(prevState.notes, [noteId]),
        [nextNote.id]: nextNote,
      };

      return {
        ...prevState,
        noteIds: nextNoteIds,
        notes: nextNotes,
        panes: nextPanes,
        activeNoteId:
          prevState.activeNoteId === noteId
            ? newNoteId
            : prevState.activeNoteId,
      };
    }
    case NotebookActionType.RESIZE_RESULTS_FOR_NOTE: {
      const { resultsHeight } = action.payload;
      const note = prevState.notes[noteId];

      const isCollapsed = !(resultsHeight > minResultsHeight);
      const nextNote = clonedeep(prevState.notes[noteId]);
      nextNote.resultSettings = {
        ...note.resultSettings,
        height: resultsHeight,
        heightPercent: null,
        isCollapsed,
      };

      return {
        ...prevState,
        notes: {
          ...prevState.notes,
          [noteId]: nextNote,
        },
      };
    }
    case NotebookActionType.SAVE_VIEW_STATE_FOR_NOTE: {
      return updateNoteState(
        prevState,
        action.payload.noteId,
        'editorSettings',
        {
          viewState: action.payload.viewState,
        }
      );
    }
    case FileSystemActionType.DID_SAVE: {
      return updateNoteState(
        prevState,
        action.payload.noteId,
        'editorSettings',
        {
          handle: action.payload.handle,
          latestSavedVersionId: action.payload.latestSavedVersionId,
          isSaved: true,
        }
      );
    }
    case StoredQueriesActionType.RENAME_QUERY_SUCCESS: {
      const { oldName, newName } = action.payload.action;

      const oldNote = Object.values(prevState.notes).find(
        (note) =>
          note.storageType === NoteStorageType.STARDOG &&
          note.target === oldName
      );

      if (!oldNote) {
        return prevState;
      }

      const newNote = clonedeep(oldNote);
      const uniqueName = getUniqueTabNameForStorageType(
        new Set(prevState.noteIds),
        oldNote.storageType,
        newName
      );
      newNote.name = uniqueName;
      const newNoteId = getTabId(newNote.storageType, uniqueName);
      newNote.id = newNoteId;
      newNote.target = newName;

      const noteIdx = prevState.noteIds.indexOf(oldNote.id);
      const nextNoteIds = [...prevState.noteIds];
      nextNoteIds.splice(noteIdx, 1, newNote.id);

      const nextPanes = Object.keys(prevState.panes).reduce<PaneStateMap>(
        (acc, key) => {
          const noteIds = [...prevState.panes[key].noteIds];
          const noteIdx = noteIds.indexOf(oldNote.id);
          noteIds.splice(noteIdx, 1, newNote.id);
          acc[key] = { noteIds };
          return acc;
        },
        {}
      );

      const nextNotes = omit(prevState.notes, oldNote.id);
      nextNotes[newNote.id] = newNote;

      return {
        ...prevState,
        noteIds: nextNoteIds,
        notes: nextNotes,
        panes: nextPanes,
        activeNoteId:
          prevState.activeNoteId === oldNote.id
            ? newNote.id
            : prevState.activeNoteId,
      };
    }
    // Stored Query Save and Save As
    // Similar to `FileSystemAction.DID_SAVE_AS` with the extra case from
    // `FileSystemAction.DID_SAVE` where the user is performing a regular
    // `Save`.
    case StoredQueriesActionType.UPDATE_QUERY_SUCCESS: {
      const { name } = action.payload.action.storedQueryOptions;

      const note = Object.values(prevState.notes).find(
        (note) =>
          note.storageType === NoteStorageType.STARDOG && note.target === name
      );

      if (!note) {
        return prevState;
      }

      return updateNoteState(prevState, note.id, 'editorSettings', {
        latestSavedVersionId: action.payload.action.latestSavedVersionId,
        isSaved: true,
      });
    }
    // Replace current tab with a new one for `newUri`. If a tab already exists
    // with that ID then update and switch to it. Retain the current (old) tab's
    // query and result settings.
    case FileSystemActionType.DID_SAVE_AS: {
      const { handle, oldUri, newUri } = action.payload;
      const oldTab = prevState.notes[oldUri];
      const stateWithoutOldTab = removeTab(prevState, oldUri);
      if (prevState.notes[newUri] && oldUri !== newUri) {
        const stateWithUpdatedQuery = updateNoteState(
          stateWithoutOldTab,
          newUri,
          'editorSettings',
          {
            handle,
            query: oldTab.editorSettings.query,
            visualizationSettingsId:
              oldTab.editorSettings.visualizationSettingsId,
          }
        );
        const stateWithFullyUpdatedTab = updateNoteState(
          stateWithUpdatedQuery,
          newUri,
          'resultSettings',
          { ...oldTab.resultSettings }
        );
        return { ...stateWithFullyUpdatedTab, activeNoteId: newUri };
      }
      const newTab = new NoteState(
        NoteStorageType.FILESYSTEM,
        newUri,
        getTabId(NoteStorageType.FILESYSTEM, newUri)
      );
      newTab.editorSettings = {
        ...newTab.editorSettings,
        // Carry the following editor settings over:
        ...pick(
          oldTab.editorSettings,
          'activeDatabase',
          'namedGraphs',
          'languageId',
          'reasoning',
          'timeout',
          'schema',
          'query',
          'visualizationSettingsId'
        ),
        handle,
      };
      newTab.resultSettings = {
        ...oldTab.resultSettings,
      };
      return addTab(stateWithoutOldTab, newTab);
    }
    case NotebookActionType.SET_SAVED_FLAG_FOR_NOTE: {
      return updateNoteState(
        prevState,
        prevState.activeNoteId,
        'editorSettings',
        {
          isSaved: action.payload.isSaved,
          changedWhileInactive: false,
        }
      );
    }
    case NotebookActionType.OPEN_IN_TAB: {
      const {
        target,
        contents,
        storageType,
        database,
        namedGraphs,
        schema,
        reasoning,
        languageId,
        handle,
      } = action.payload;
      // Does not pass `target` to `getNewTabId` because this action does not
      // assume the tab to open is a new tab -- it may already be present, which
      // will be handled by `addTab`
      const newTab = new NoteState(
        storageType || NoteStorageType.NONE,
        target || getUntitledTabName(prevState),
        getTabId(
          storageType || NoteStorageType.NONE,
          target || getUntitledTabName(prevState)
        )
      );
      newTab.editorSettings.query = contents;
      newTab.editorSettings.reasoning = reasoning;
      newTab.isUntitled = !target;
      if (target) {
        newTab.target = target;
      }
      if (handle) {
        newTab.editorSettings.handle = handle;
      }
      if (languageId) {
        newTab.editorSettings.languageId = languageId;
      }
      if (database && database !== GLOBAL_SYMBOL) {
        newTab.editorSettings.activeDatabase = database;
      }
      if (namedGraphs && namedGraphs.length) {
        newTab.editorSettings.namedGraphs = namedGraphs;
      }
      if (schema) {
        newTab.editorSettings.schema = schema;
      }
      return addTab(prevState, newTab);
    }
    case NotebookActionType.UPDATE_NOTEBOOK_MOSAIC_STATE: {
      const { mosaicStateChange } = action.payload;
      return {
        ...prevState,
        mosaicState: {
          ...prevState.mosaicState,
          ...mosaicStateChange,
        },
      };
    }
    case NotebookActionType.SET_STORED_AND_HISTORY_TAB: {
      const { storedAndHistoryTab } = action.payload;
      return {
        ...prevState,
        storedAndHistoryTab,
      };
    }
    case NotebookActionType.SET_RESULTS_COLUMN_WIDTHS: {
      const { columnWidths } = action.payload;
      return updateNoteState(
        prevState,
        prevState.activeNoteId,
        'resultSettings',
        {
          columnWidths,
        }
      );
    }
    case NotebookActionType.SET_RESULTS_SCROLL_POSITION: {
      const { scrollPosition } = action.payload;
      return updateNoteState(prevState, noteId, 'resultSettings', {
        scrollPosition,
      });
    }
    case NotebookActionType.SET_RESULTS_SELECTION: {
      const { selection } = action.payload;
      return updateNoteState(
        prevState,
        prevState.activeNoteId,
        'resultSettings',
        {
          selection,
        }
      );
    }
    case NotebookActionType.SET_RESULTS_VIEW_TYPE: {
      const { viewType } = action.payload;
      return updateNoteState(
        prevState,
        prevState.activeNoteId,
        'resultSettings',
        {
          viewType,
        }
      );
    }
    case NotebookActionType.TOGGLE_STORE_QUERY_POPOVER: {
      return updateNoteState(prevState, noteId, 'editorSettings', {
        isStoreQueryPopoverOpen:
          !prevState.notes[noteId].editorSettings.isStoreQueryPopoverOpen,
      });
    }
    case NotebookActionType.SET_QUERY_CONFIG_POPOVER_OPEN: {
      return updateNoteState(prevState, noteId, 'editorSettings', {
        isQueryConfigPopoverOpen: action.payload.isOpen,
      });
    }
    case NotebookActionType.SET_SCHEMA_DIALOG_OPEN:
      return updateNoteState(prevState, noteId, 'editorSettings', {
        isSchemaDialogOpen: action.payload.isOpen,
      });
    // Store query button for filesystem queries
    case StoredQueriesActionType.STORE_QUERY_SUCCESS: {
      const { activeNoteId, noteIds } = prevState;
      const { storeQueryOptions } = action.payload.action;

      const prevNote = prevState.notes[activeNoteId];

      const newTabName = getUniqueTabNameForStorageType(
        new Set(noteIds),
        NoteStorageType.STARDOG,
        storeQueryOptions.name
      );
      const newTab = new NoteState(
        NoteStorageType.STARDOG,
        newTabName,
        getTabId(NoteStorageType.STARDOG, newTabName)
      );
      newTab.target = storeQueryOptions.name;
      newTab.editorSettings.query = storeQueryOptions.query;
      newTab.editorSettings.languageId =
        prevNote.editorSettings.languageId || LanguageId.SPARQL;
      newTab.editorSettings.visualizationSettingsId =
        prevNote.editorSettings.visualizationSettingsId;
      newTab.resultSettings = { ...prevNote.resultSettings };
      if (prevNote.isUntitled) {
        const stateWithStoredTab = addTab(prevState, newTab);
        return removeTab(stateWithStoredTab, activeNoteId);
      }
      const updatedActiveNoteState = updateNoteState(
        prevState,
        activeNoteId,
        'editorSettings',
        { isStoreQueryPopoverOpen: false }
      );
      return addTab(updatedActiveNoteState, newTab);
    }
    case StoredQueriesActionType.DELETE_STORED_QUERY_SUCCESS: {
      const noteId = Object.keys(prevState.notes).find((noteId) => {
        const note = prevState.notes[noteId];
        return (
          note.storageType === NoteStorageType.STARDOG &&
          note.target === action.payload.action.queryName
        );
      });

      if (!noteId) {
        return prevState;
      }

      return removeTab(prevState, noteId);
    }
    case NotebookActionType.RESET_SELECTED_DATABASES: {
      const { databases } = action.payload;
      return {
        ...prevState,
        notes: Object.keys(prevState.notes).reduce((acc, id) => {
          const prevNamedGraphsForNote =
            prevState.notes[id].editorSettings.namedGraphs;
          let nextActiveDatabaseIdForNote: string;
          let nextNamedGraphsForNote: string[];
          const prevActiveDatabaseIdForNote =
            prevState.notes[id].editorSettings.activeDatabase;
          if (databases.includes(prevActiveDatabaseIdForNote)) {
            nextActiveDatabaseIdForNote = prevActiveDatabaseIdForNote;
            nextNamedGraphsForNote = [...prevNamedGraphsForNote];
          } else {
            nextActiveDatabaseIdForNote = '';
            nextNamedGraphsForNote = [];
          }

          return {
            ...acc,
            [id]: {
              ...prevState.notes[id],
              editorSettings: {
                ...prevState.notes[id].editorSettings,
                activeDatabase: nextActiveDatabaseIdForNote,
                namedGraphs: nextNamedGraphsForNote,
              },
            },
          };
        }, {}),
      };
    }
    case NotebookActionType.SET_LANGUAGE_ID_FOR_NOTE: {
      const { noteId, languageId } = action.payload;
      const editorSettingsUpdate: Partial<EditorSettings> = { languageId };
      // if we're switching to GraphQL, remove the named graph
      if (languageId === LanguageId.GRAPHQL) {
        editorSettingsUpdate.namedGraphs = [];
      }
      return updateNoteState(
        prevState,
        noteId,
        'editorSettings',
        editorSettingsUpdate
      );
    }
    case NotebookActionType.OPEN_RELEASE_NOTES: {
      return addTab(prevState, RELEASE_NOTES_NOTE);
    }
    case NotebookActionType.OPEN_USER_PREFERENCES: {
      const { preferences } = action.payload;
      const newNote = clonedeep(PREFERENCES_NOTE);
      newNote.editorSettings.query = getPreferencesQuery(preferences);
      return addTab(prevState, newNote);
    }
    case NotebookActionType.LOAD_SANDDANCE_ATTEMPT: {
      const { noteId } = action.payload;
      const { height } = prevState.notes[noteId].resultSettings;
      let heightPercent: number = null;
      let heightPercentMaxHeight: number = null;
      if (height < defaultSanddanceResultsHeight) {
        heightPercent = 80;
        heightPercentMaxHeight = defaultSanddanceResultsHeight;
      }
      return updateNoteState(prevState, noteId, 'resultSettings', {
        pendingSandDance: true,
        height,
        heightPercent,
        heightPercentMaxHeight,
      });
    }
    case NotebookActionType.LOAD_SANDDANCE_SUCCESS:
    case NotebookActionType.LOAD_SANDDANCE_FAILURE: {
      const { noteId } = action.payload;
      return updateNoteState(prevState, noteId, 'resultSettings', {
        pendingSandDance: false,
      });
    }
    // The UserPreferences note does not share the same ManagedEditor as the
    // other the Note components, so its ManagedEditor will not be aware about
    // needing to check its state while the note is inactive. Therefore, we need
    // to update the note state when the set preferences actions are dispatched
    case PreferencesActionType.SET_PREFERENCE: {
      const note = prevState.notes[PREFERENCES_NOTE_ID];
      if (!note) {
        return prevState;
      }
      const preferences = {
        ...action.payload.preferences,
        [action.payload.key]: action.payload.value,
      };
      const query = JSON.stringify(preferences, null, 2);
      // We only want to override the contents of the user preferences note
      // if the note is not currently being edited (that is, in a saved state)
      if (note.editorSettings.isSaved) {
        return updateNoteState(
          prevState,
          PREFERENCES_NOTE_ID,
          'editorSettings',
          {
            query,
            isSaved: true,
          }
        );
      }
      // If the change results in the same value of the current note,
      // we update the isSaved flag of the note
      if (note.editorSettings.query === query) {
        return updateNoteState(
          prevState,
          PREFERENCES_NOTE_ID,
          'editorSettings',
          {
            isSaved: true,
          }
        );
      }
      // Otherwise, no state change is necessary
      return prevState;
    }
    case PreferencesActionType.SET_PREFERENCES: {
      const { preferences } = action.payload;
      const query = getPreferencesQuery(preferences);
      // Currently, set preferences is only dispatched at the request
      // of "saving" the user prefrences file, so we don't need to
      // worry about the note contents being out of sync
      return updateNoteState(prevState, PREFERENCES_NOTE_ID, 'editorSettings', {
        query,
        isSaved: true,
      });
    }
    default:
      return prevState;
  }
};
